import React, { useMemo } from 'react'
import { Freessrs, Journey, Segment } from '../../api'
import {
  calculateTotalMinutes,
  formatDate,
  formatDuration,
  formatTime,
} from '../../utils/FlightUtil'

interface AirportInfoProps {
  date: string
  airport: string
  isArrival?: boolean
}

const AirportInfo: React.FC<AirportInfoProps> = React.memo(({ date, airport, isArrival }) => {
  return (
    <div style={isArrival ? { textAlign: 'right' } : undefined}>
      <div style={{ fontSize: '14px' }}>{formatDate(date)}</div>
      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatTime(date)}</div>
      <div style={{ fontSize: '12px', color: 'gray' }}>{airport}</div>
    </div>
  )
})

interface FlightPathProps {
  segments: Segment[]
}

const FlightPath: React.FC<FlightPathProps> = React.memo(({ segments }) => {
  const stopoverCount = segments.length - 1

  if (stopoverCount <= 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'lightgray' }}></div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <div style={{ flex: 1, height: '1px', backgroundColor: 'lightgray' }}></div>
      {segments.slice(0, -1).map((_, idx) => (
        <React.Fragment key={idx}>
          <div
            style={{
              width: '8px',
              height: '8px',
              backgroundColor: 'var(--vela-primary-color)',
              borderRadius: '2px',
              margin: '0 4px',
            }}
          />
          <div style={{ flex: 1, height: '1px', backgroundColor: 'lightgray' }}></div>
        </React.Fragment>
      ))}
    </div>
  )
})

interface BaggageInfoProps {
  freessrs: Freessrs[] | undefined
}

const BaggageInfo: React.FC<BaggageInfoProps> = React.memo(({ freessrs }) => {
  const commonLabel = '위탁 수하물 '

  const baggageInfo = freessrs?.find(
    ssr => ssr.ssrType === 'Baggage' && ssr.ssrService.some(service => service.paxType === 'ADT'),
  )

  const adtService = baggageInfo?.ssrService.find(service => service.paxType === 'ADT')

  if (!adtService) {
    return (
      <span>
        {commonLabel}
        <span style={{ color: 'var(--vela-card-warning-text-color)' }}>불포함</span>
      </span>
    )
  }

  return (
    <span>
      {commonLabel}
      <span style={{ color: 'var(--vela-primary-color)' }}>{`${adtService.quantity}개 포함`}</span>
    </span>
  )
})

interface JourneyCardProps {
  journey: Journey
  isLast: boolean
}

const JourneyCard: React.FC<JourneyCardProps> = React.memo(({ journey, isLast }) => {
  const totalMinutes = useMemo(() => calculateTotalMinutes(journey.segments), [journey.segments])
  const duration = useMemo(() => formatDuration(totalMinutes), [totalMinutes])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: isLast ? '0' : '8px',
        padding: '8px',
        borderBottom: isLast ? 'none' : '1px solid #eee',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={{ fontWeight: 'bold' }}>{journey.airlineName || journey.airline}</div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <AirportInfo date={journey.departureDate} airport={journey.deptAirport} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '8px 0',
            }}
          >
            <div style={{ fontSize: '12px', color: 'gray', marginBottom: '4px' }}>{duration}</div>
            <FlightPath segments={journey.segments} />
          </div>
        </div>

        <AirportInfo date={journey.arrivalDate} airport={journey.arrAirport} isArrival />
      </div>

      <div
        style={{
          marginTop: '8px',
          fontSize: '12px',
          backgroundColor: 'whitesmoke',
          padding: '4px 8px',
          borderRadius: '4px',
        }}
      >
        <BaggageInfo freessrs={journey.freessrs} />
      </div>
    </div>
  )
})

export default JourneyCard
export type { JourneyCardProps }
